import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
} from "@mui/material";
import { Close, CloudUpload as UploadIcon } from "@mui/icons-material";
import CustomTextField from "../../../components/ui/Textfield";
import ColorPicker from "../../../components/ColorPicker";
import { ICoverTemplate } from "./types/icover-template";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    form: FormData,
    editing?: ICoverTemplate | null
  ) => Promise<void>;
  editingTemplate?: ICoverTemplate | null;
  loading: boolean;
}

const CoverTemplateDialog: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  editingTemplate,
  loading,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    category: "FUNERAL",
    isActive: true,
    them_color: "#000000",
    templateImageUrl: "",
    backgroundImageUrl: "",
  });

  useEffect(() => {
    if (editingTemplate) {
      setFormData({
        name: editingTemplate.name,
        category: editingTemplate.category,
        isActive: editingTemplate.is_active,
        them_color: editingTemplate.text_color || "#000000",
        templateImageUrl: editingTemplate.foreground_image || "",
        backgroundImageUrl: editingTemplate.background_image || "",
      });
    } else {
      setFormData({
        name: "",
        category: "funeral",
        isActive: true,
        them_color: "#000000",
        templateImageUrl: "",
        backgroundImageUrl: "",
      });
    }
  }, [editingTemplate]);

  const handleFileUpload = (file: File, type: "template" | "background") => {
    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      [type === "template" ? "templateImageUrl" : "backgroundImageUrl"]:
        previewUrl,
    }));
    if (type === "template") setTemplateFile(file);
    else setBackgroundFile(file);
  };

  const handleSubmit = async () => {
    const form = new FormData();
    form.append("name", formData.name);
    form.append("category", formData.category);
    form.append("is_active", formData.isActive.toString());
    form.append("them_color", formData.them_color);
    if (templateFile) form.append("foreground_image", templateFile);
    if (backgroundFile) form.append("background_image", backgroundFile);
    await onSubmit(form, editingTemplate);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <DialogTitle>
          {editingTemplate ? "Edit Cover Template" : "Add New Cover Template"}
        </DialogTitle>
        <IconButton onClick={onClose} sx={{ mr: 1 }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              label="Template Name"
              fullWidth
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <Select
                label="Category"
                value={formData.category}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    category: e.target.value,
                  }))
                }
              >
              <MenuItem value="funeral">Funeral</MenuItem>
              <MenuItem value="church">Church</MenuItem>
              <MenuItem value="conference">Conference</MenuItem>
              <MenuItem value="event">Event</MenuItem>
              <MenuItem value="generic">Generic</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />
          </Grid>

          {/* Upload fields */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2">Foreground Image</Typography>
            <Button
              component="label"
              variant="outlined"
              fullWidth
              startIcon={<UploadIcon />}
            >
              Upload
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) =>
                  e.target.files &&
                  handleFileUpload(e.target.files[0], "template")
                }
              />
            </Button>
            {formData.templateImageUrl && (
              <Box mt={1}>
                <img
                  src={formData.templateImageUrl}
                  alt="Preview"
                  style={{ width: "100%", height: "120px", objectFit: "cover" }}
                />
              </Box>
            )}
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2">Background Image</Typography>
            <Button
              component="label"
              variant="outlined"
              fullWidth
              startIcon={<UploadIcon />}
            >
              Upload
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) =>
                  e.target.files &&
                  handleFileUpload(e.target.files[0], "background")
                }
              />
            </Button>
            {formData.backgroundImageUrl && (
              <Box mt={1}>
                <img
                  src={formData.backgroundImageUrl}
                  alt="Preview"
                  style={{ width: "100%", height: "120px", objectFit: "cover" }}
                />
              </Box>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2">Theme Color</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                onClick={() => setShowColorPicker((p) => !p)}
              >
                Choose Color
              </Button>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  backgroundColor: formData.them_color,
                  border: "1px solid #ccc",
                }}
              />
            </Box>
            {showColorPicker && (
              <Box mt={2}>
                <ColorPicker
                  label="Color"
                  value={formData.them_color}
                  onChange={(newColor) =>
                    setFormData((prev) => ({ ...prev, them_color: newColor }))
                  }
                />
              </Box>
            )}
          </Grid>
        </Grid>

        {loading && (
          <Box display="flex" alignItems="center" mt={2}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography>Saving...</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name}
        >
          {editingTemplate ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverTemplateDialog;
